import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Returns a "go back" handler that behaves like the browser back button while
 * the user is navigating WITHIN the app, but falls back to a safe route (Home
 * by default) when there is no in-app history to return to.
 *
 * This covers the case where a page (e.g. /login, /signup) is opened directly
 * — via a bookmark, a shared link, or a fresh tab — where a plain
 * `navigate(-1)` / `window.history.back()` would take the user OUT of the site
 * to the browser's start screen or the previously visited external page.
 *
 * Detection: React Router (BrowserRouter) stamps an incrementing `idx` onto
 * `window.history.state` for every in-app entry. `idx > 0` means there is a
 * previous app entry to go back to; `0` (or missing) means this is the first
 * entry of the session, so we route to the fallback instead.
 *
 * @param {string} fallback - Route to go to when there's no app history.
 */
export const useGoBack = (fallback = "/") => {
  const navigate = useNavigate();

  return useCallback(() => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  }, [navigate, fallback]);
};

export default useGoBack;
